import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Settings, TrendingUp, ShieldCheck, CheckCircle, 
  MessageSquare, Headphones
} from 'lucide-react';
import { companyInfo, whyUsPoints, investmentProcess } from '../data/mock';

const WhyUs = () => {
  const handleWhatsApp = () => {
    window.open(companyInfo.whatsappLink, '_blank');
  };

  const getIcon = (iconName) => {
    const icons = {
      Settings, TrendingUp, ShieldCheck, CheckCircle, MessageSquare, Headphones
    };
    const Icon = icons[iconName] || CheckCircle;
    return <Icon className="w-8 h-8" />;
  };

  return (
    <div className="min-h-screen bg-[#0f1419] pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1f2e] to-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">WealthOptimizers</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Discover what sets us apart in wealth management and financial advisory
            </p>
          </div>
        </div>
      </section>

      {/* Why Us Points */}
      <section className="py-20 bg-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Differentiators</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              What makes us the preferred choice for corporate professionals
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyUsPoints.map((point) => (
              <Card key={point.id} className="bg-[#1a1f2e] border-gray-800 hover:border-[#F59E0B] transition-all hover:shadow-lg hover:shadow-[#F59E0B]/10 group">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-[#F59E0B]/10 rounded-lg flex items-center justify-center text-[#F59E0B] mb-6 group-hover:bg-[#F59E0B]/20 transition-colors">
                    {getIcon(point.icon)}
                  </div>
                  <h3 className="text-2xl font-semibold mb-4 text-white">
                    {point.title}
                  </h3>
                  <p className="text-[17px] text-gray-300 leading-relaxed">
                    {point.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Philosophy */}
      <section className="py-20 bg-gradient-to-br from-[#1a1f2e] to-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Our Investment <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Philosophy</span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                We believe in time-tested principles that have consistently delivered results for our clients.
              </p>
              <ul className="space-y-4">
                {[
                  "Long-term wealth creation over short-term speculation",
                  "Disciplined, systematic investing through SIPs",
                  "Risk-aligned portfolio construction",
                  "Diversification across asset classes",
                  "Regular portfolio reviews and rebalancing",
                  "Transparent, investor-first approach",
                  "Full compliance with all regulatory requirements"
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-[#F59E0B] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-lg text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1639312091690-8b26d223c5a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxJbmRpYW4lMjBidXNpbmVzcyUyMHByb2Zlc3Npb25hbHN8ZW58MHx8fHwxNzcwMjc2OTIxfDA&ixlib=rb-4.1.0&q=85"
                alt="Investment Philosophy"
                className="rounded-2xl shadow-xl w-full h-auto border border-gray-800"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Investment Process */}
      <section className="py-20 bg-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Our Investment <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Process</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              A structured, proven approach to helping you achieve your financial goals
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            {investmentProcess.map((process, index) => (
              <div key={process.step} className="relative">
                <div className="flex items-start mb-12">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#F59E0B] to-[#FBBF24] rounded-full flex items-center justify-center text-black font-bold text-2xl shadow-lg shadow-[#F59E0B]/30">
                      {process.step}
                    </div>
                  </div>
                  <div className="ml-8 flex-1 bg-[#1a1f2e] p-6 rounded-lg border border-gray-800">
                    <h3 className="text-2xl font-semibold mb-3 text-white">
                      {process.title}
                    </h3>
                    <p className="text-lg text-gray-300 leading-relaxed">
                      {process.description}
                    </p>
                  </div>
                </div>
                {index < investmentProcess.length - 1 && (
                  <div className="absolute left-8 top-16 bottom-0 w-0.5 bg-gradient-to-b from-[#F59E0B] to-[#FBBF24] -mb-12 opacity-30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1f2e] to-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center text-white mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Commitment</span> to You
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                title: "Unbiased Advice",
                description: "We recommend only those products that are truly suitable for your financial situation and goals. No product pushing, no hard selling."
              },
              {
                title: "Regular Communication",
                description: "Stay informed with periodic portfolio updates, market insights, and proactive communication during market volatility."
              },
              {
                title: "Goal Tracking",
                description: "Monitor progress towards each of your financial goals with clear metrics and timely course corrections when needed."
              },
              {
                title: "Lifetime Partnership",
                description: "We're not here for one-time transactions. We build long-term relationships and support you through all life stages."
              }
            ].map((item, index) => (
              <Card key={index} className="bg-[#1a1f2e] border-[#F59E0B]/30">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-semibold mb-4 text-white">
                    {item.title}
                  </h3>
                  <p className="text-[17px] text-gray-300 leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { value: "12+", label: "Years Experience" },
              { value: "All India", label: "Service Coverage" },
              { value: "AMFI", label: "Registered & Compliant" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] mb-2">
                  {stat.value}
                </div>
                <div className="text-lg text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1f2e] to-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <Card className="bg-gradient-to-r from-[#F59E0B]/10 to-[#FBBF24]/10 border-2 border-[#F59E0B]/30">
            <CardContent className="p-12 text-center">
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Experience the WealthOptimizers Difference
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Join corporate professionals who trust us with their financial future
              </p>
              <Button
                onClick={handleWhatsApp}
                size="lg"
                className="bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] hover:from-[#D97706] hover:to-[#F59E0B] text-black font-semibold px-10 py-6 text-lg"
              >
                Start Your Journey Today
              </Button>
              <p className="text-sm text-gray-500 mt-6">
                {companyInfo.disclaimer}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default WhyUs;